<template>
    <LoadingComponent :props="loading" />
    <SmModalCreateComponent :props="addButton" />

    <div id="modal" class="modal">
        <div class="modal-dialog">
            <div class="modal-header">
                <h3 class="modal-title">{{ $t("menu.languages") }}</h3>
                <button class="modal-close fa-solid fa-xmark text-xl text-slate-400 hover:text-red-500"
                    @click="reset"></button>
            </div>
            <div class="modal-body">
                <form @submit.prevent="save">
                    <div class="form-row">
                        <div class="form-col-12 sm:form-col-6">
                            <label for="name" class="db-field-title required">
                                {{ $t("label.name") }}
                            </label>
                            <input v-model="props.form.name" v-bind:class="errors.name ? 'invalid' : ''" type="text"
                                id="name" class="db-field-control" />
                            <small class="db-field-alert" v-if="errors.name">
                                {{ errors.name[0] }}
                            </small>
                        </div>

                        <div class="form-col-12 sm:form-col-6">
                            <label for="code" class="db-field-title required">
                                {{ $t("label.code") }}
                            </label>
                            <input v-model="props.form.code" v-bind:class="errors.code ? 'invalid' : ''" type="text"
                                id="code" class="db-field-control" @input="onCodeInput" />
                            <small class="db-field-alert" v-if="errors.code">
                                {{ errors.code[0] }}
                            </small>
                        </div>

                        <!-- SECCIÓN DE IMAGEN / BANDERA -->
                        <div class="form-col-12 sm:form-col-12">
                            <label class="db-field-title">
                                {{ $t("label.image") }}
                            </label>

                            <!-- Selector de origen -->
                            <div class="db-field-radio-group flex flex-wrap items-center gap-6 mb-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                                <div class="db-field-radio flex items-center gap-2 cursor-pointer">
                                    <div class="custom-radio">
                                        <input type="radio" id="img_src_default" v-model="imageSource" value="default" class="custom-radio-field" />
                                        <span class="custom-radio-span"></span>
                                    </div>
                                    <label for="img_src_default" class="db-field-label text-xs font-semibold text-slate-700 cursor-pointer">
                                        Banderas predeterminadas (SVG)
                                    </label>
                                </div>

                                <div class="db-field-radio flex items-center gap-2 cursor-pointer">
                                    <div class="custom-radio">
                                        <input type="radio" id="img_src_upload" v-model="imageSource" value="upload" class="custom-radio-field" />
                                        <span class="custom-radio-span"></span>
                                    </div>
                                    <label for="img_src_upload" class="db-field-label text-xs font-semibold text-slate-700 cursor-pointer">
                                        Subir desde el explorador
                                    </label>
                                </div>
                            </div>

                            <!-- MODO A: BANDERAS PRESET SVG (SELECTOR PERSONALIZADO NATIVO VUE) -->
                            <div v-if="imageSource === 'default'" class="space-y-3">
                                <div class="relative">
                                    <button
                                        type="button"
                                        @click="isFlagDropdownOpen = !isFlagDropdownOpen"
                                        class="w-full h-[42px] px-3 py-2 bg-white border border-slate-200 rounded-lg flex items-center justify-between text-left text-sm transition focus:outline-none focus:border-primary shadow-xs"
                                    >
                                        <div class="flex items-center gap-2.5 overflow-hidden">
                                            <template v-if="selectedFlagCode">
                                                <img :src="'/images/default/language/flags/' + selectedFlagCode + '.svg'" class="w-6 h-4 object-cover rounded-xs border border-slate-200 flex-shrink-0" alt="flag" />
                                                <span class="font-bold text-slate-800 uppercase text-xs">{{ selectedFlagCode }}</span>
                                                <span class="text-xs text-slate-500 truncate">({{ getFlagName(selectedFlagCode) }})</span>
                                            </template>
                                            <template v-else>
                                                <span class="text-slate-400 font-normal text-xs">Seleccionar una bandera de la lista...</span>
                                            </template>
                                        </div>
                                        <i class="fa-solid fa-chevron-down text-xs text-slate-400 transition-transform" :class="{ 'rotate-180': isFlagDropdownOpen }"></i>
                                    </button>

                                    <!-- Menú Desplegable con Buscador en Tiempo Real -->
                                    <div
                                        v-if="isFlagDropdownOpen"
                                        class="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50 p-2 space-y-2 max-h-64 flex flex-col"
                                    >
                                        <div class="relative flex-shrink-0">
                                            <i class="fa-solid fa-magnifying-glass absolute left-3 top-2.5 text-xs text-slate-400"></i>
                                            <input
                                                type="text"
                                                v-model="flagSearch"
                                                placeholder="Buscar por país o código (ej: MX, ES, US)..."
                                                class="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:border-primary"
                                            />
                                        </div>

                                        <div class="overflow-y-auto flex-1 divide-y divide-slate-50 max-h-48">
                                            <button
                                                v-for="flag in filteredFlagOptions"
                                                :key="flag.code"
                                                type="button"
                                                @click="onSelectFlagCustom(flag.code)"
                                                class="w-full px-2.5 py-2 text-left flex items-center gap-3 hover:bg-slate-50 rounded-lg transition"
                                                :class="{ 'bg-primary/10 font-bold text-primary': selectedFlagCode === flag.code }"
                                            >
                                                <img :src="'/images/default/language/flags/' + flag.code + '.svg'" class="w-6 h-4 object-cover rounded-xs border border-slate-200 flex-shrink-0" alt="flag" />
                                                <span class="text-xs font-bold uppercase w-10 text-slate-700">{{ flag.code }}</span>
                                                <span class="text-xs text-slate-600 flex-1 truncate">{{ flag.name }}</span>
                                                <i v-if="selectedFlagCode === flag.code" class="fa-solid fa-check text-xs text-primary"></i>
                                            </button>

                                            <div v-if="filteredFlagOptions.length === 0" class="py-4 text-center text-xs text-slate-400">
                                                No se encontraron banderas para "{{ flagSearch }}"
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Botones rápidos de sugerencias -->
                                <div class="flex flex-wrap items-center gap-1.5 pt-1">
                                    <span class="text-[11px] text-slate-400 font-semibold mr-1">Frecuentes:</span>
                                    <button
                                        v-for="flag in popularFlags"
                                        :key="flag.code"
                                        type="button"
                                        @click="onSelectFlagCustom(flag.code)"
                                        class="px-2 py-1 border rounded-md text-xs font-medium flex items-center gap-1.5 transition hover:bg-slate-100"
                                        :class="selectedFlagCode === flag.code ? 'border-primary bg-primary/10 text-primary font-bold' : 'border-slate-200 text-slate-600 bg-white'"
                                    >
                                        <img :src="'/images/default/language/flags/' + flag.code + '.svg'" class="w-4 h-2.5 object-cover rounded-xs border border-slate-200" />
                                        <span class="uppercase text-[11px]">{{ flag.code }}</span>
                                    </button>
                                </div>
                            </div>

                            <!-- MODO B: SUBIR ARCHIVO DESDE LA COMPUTADORA -->
                            <div v-else>
                                <input @change="changeImage" v-bind:class="errors.image ? 'invalid' : ''" id="image" type="file"
                                    class="db-field-control" ref="imageProperty" accept="image/png, image/jpeg, image/jpg, image/svg+xml, .svg" />
                                <small class="text-xs text-slate-400 block mt-1">Formatos soportados: PNG, JPG, JPEG, SVG.</small>
                            </div>

                            <small class="db-field-alert" v-if="errors.image">
                                {{ errors.image[0] }}
                            </small>
                        </div>

                        <div class="form-col-12 sm:form-col-12">
                            <label class="db-field-title required" for="ltr">{{ $t("label.reading_direction") }}</label>
                            <div class="db-field-radio-group">
                                <div class="db-field-radio">
                                    <div class="custom-radio">
                                        <input :value="enums.displayModeEnum.LTR" v-model="props.form.display_mode" id="ltr"
                                            type="radio" class="custom-radio-field" />
                                        <span class="custom-radio-span"></span>
                                    </div>
                                    <label for="ltr" class="db-field-label">{{ $t("label.ltr") }}</label>
                                </div>
                                <div class="db-field-radio">
                                    <div class="custom-radio">
                                        <input :value="enums.displayModeEnum.RTL" v-model="props.form.display_mode"
                                            type="radio" id="rtl" class="custom-radio-field" />
                                        <span class="custom-radio-span"></span>
                                    </div>
                                    <label for="rtl" class="db-field-label">{{ $t("label.rtl") }}</label>
                                </div>
                            </div>
                        </div>

                        <div class="form-col-12 sm:form-col-6">
                            <label class="db-field-title required" for="active">{{ $t("label.status") }}</label>
                            <div class="db-field-radio-group">
                                <div class="db-field-radio">
                                    <div class="custom-radio">
                                        <input :value="enums.statusEnum.ACTIVE" v-model="props.form.status" id="active"
                                            type="radio" class="custom-radio-field" />
                                        <span class="custom-radio-span"></span>
                                    </div>
                                    <label for="active" class="db-field-label">{{ $t("label.active") }}</label>
                                </div>
                                <div class="db-field-radio">
                                    <div class="custom-radio">
                                        <input :value="enums.statusEnum.INACTIVE" v-model="props.form.status" type="radio"
                                            id="inactive" class="custom-radio-field" />
                                        <span class="custom-radio-span"></span>
                                    </div>
                                    <label for="inactive" class="db-field-label">{{ $t("label.inactive") }}</label>
                                </div>
                            </div>
                        </div>

                        <div class="form-col-12">
                            <div class="modal-btns">
                                <button type="button" class="modal-btn-outline modal-close" @click="reset">
                                    <i class="lab lab-fill-close-circle"></i>
                                    <span>{{ $t("button.close") }}</span>
                                </button>

                                <button type="submit" class="db-btn py-2 text-white bg-primary">
                                    <i class="lab lab-fill-save"></i>
                                    <span>{{ $t("button.save") }}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>
<script>
import SmModalCreateComponent from "../../components/buttons/SmModalCreateComponent";
import LoadingComponent from "../../components/LoadingComponent";
import statusEnum from "../../../../enums/modules/statusEnum";
import displayModeEnum from "../../../../enums/modules/displayModeEnum";
import alertService from "../../../../services/alertService";
import appService from "../../../../services/appService";

const flagNames = {
    es: 'España / Español',
    mx: 'México',
    us: 'Estados Unidos',
    gb: 'Reino Unido',
    'gb-eng': 'Inglaterra',
    'gb-sct': 'Escocia',
    'gb-wls': 'Gales',
    'gb-nir': 'Irlanda del Norte',
    fr: 'Francia',
    de: 'Alemania',
    it: 'Italia',
    pt: 'Portugal',
    br: 'Brasil',
    co: 'Colombia',
    ar: 'Argentina',
    cl: 'Chile',
    pe: 'Perú',
    ve: 'Venezuela',
    ec: 'Ecuador',
    gt: 'Guatemala',
    cr: 'Costa Rica',
    cu: 'Cuba',
    do: 'República Dominicana',
    uy: 'Uruguay',
    py: 'Paraguay',
    bo: 'Bolivia',
    hn: 'Honduras',
    sv: 'El Salvador',
    ni: 'Nicaragua',
    pa: 'Panamá',
    pr: 'Puerto Rico',
    ca: 'Canadá',
    cn: 'China',
    jp: 'Japón',
    kr: 'Corea del Sur',
    ru: 'Rusia',
    in: 'India',
    nl: 'Países Bajos',
    be: 'Bélgica',
    ch: 'Suiza',
    at: 'Austria',
    se: 'Suecia',
    no: 'Noruega',
    fi: 'Finlandia',
    dk: 'Dinamarca',
    pl: 'Polonia',
    gr: 'Grecia',
    tr: 'Turquía',
    sa: 'Arabia Saudita',
    ae: 'Emiratos Árabes',
    au: 'Australia',
    nz: 'Nueva Zelanda',
};

const allFlagCodes = [
    'ad','ae','af','ag','ai','al','am','ao','aq','ar','as','at','au','aw','ax','az',
    'ba','bb','bd','be','bf','bg','bh','bi','bj','bl','bm','bn','bo','bq','br','bs','bt','bv','bw','by','bz',
    'ca','cc','cd','cf','cg','ch','ci','ck','cl','cm','cn','co','cr','cu','cv','cw','cx','cy','cz',
    'de','dj','dk','dm','do','dz','ec','ee','eg','eh','er','es','et','eu','fi','fj','fk','fm','fo','fr',
    'ga','gb','gb-eng','gb-nir','gb-sct','gb-wls','gd','ge','gf','gg','gh','gi','gl','gm','gn','gp','gq','gr','gs','gt','gu','gw','gy',
    'hk','hm','hn','hr','ht','hu','id','ie','il','im','in','io','iq','ir','is','it','je','jm','jo','jp',
    'ke','kg','kh','ki','km','kn','kp','kr','kw','ky','kz','la','lb','lc','li','lk','lr','ls','lt','lu','lv','ly',
    'ma','mc','md','me','mf','mg','mh','mk','ml','mm','mn','mo','mp','mq','mr','ms','mt','mu','mv','mw','mx','my','mz',
    'na','nc','ne','nf','ng','ni','nl','no','np','nr','nu','nz','om','pa','pe','pf','pg','ph','pk','pl','pm','pn','pr','ps','pt','pw','py',
    'qa','re','ro','rs','ru','rw','sa','sb','sc','sd','se','sg','sh','si','sj','sk','sl','sm','sn','so','sr','ss','st','sv','sx','sy','sz',
    'tc','td','tf','tg','th','tj','tk','tl','tm','tn','to','tr','tt','tv','tw','tz','ua','ug','um','un','us','uy','uz',
    'va','vc','ve','vg','vi','vn','vu','wf','ws','ye','yt','za','zm','zw'
];

export default {
    name: "LanguageCreateComponent",
    components: { SmModalCreateComponent, LoadingComponent },
    props: ["props"],
    data() {
        return {
            loading: {
                isActive: false,
            },
            enums: {
                statusEnum: statusEnum,
                displayModeEnum: displayModeEnum,
                statusEnumArray: {
                    [statusEnum.ACTIVE]: this.$t("label.active"),
                    [statusEnum.INACTIVE]: this.$t("label.inactive"),
                },
                displayModeEnumArray: {
                    [displayModeEnum.LTR]: this.$t("label.ltr"),
                    [displayModeEnum.RTL]: this.$t("label.rtl"),
                },
            },
            imageSource: 'default',
            selectedFlagCode: null,
            selectedFlagPreviewUrl: null,
            isFlagDropdownOpen: false,
            flagSearch: '',
            image: "",
            errors: {},
            popularFlags: [
                { code: 'es', label: 'España / Español' },
                { code: 'mx', label: 'México' },
                { code: 'us', label: 'Estados Unidos' },
                { code: 'gb', label: 'Reino Unido' },
                { code: 'fr', label: 'Francia' },
                { code: 'de', label: 'Alemania' },
                { code: 'it', label: 'Italia' },
                { code: 'pt', label: 'Portugal' },
                { code: 'br', label: 'Brasil' },
                { code: 'co', label: 'Colombia' },
                { code: 'ar', label: 'Argentina' },
                { code: 'cl', label: 'Chile' },
                { code: 'pe', label: 'Perú' },
            ]
        };
    },
    computed: {
        addButton: function () {
            return { title: this.$t("button.add_language") };
        },
        allFlagOptions: function () {
            return allFlagCodes.map(code => ({
                id: code,
                code: code,
                name: flagNames[code] || code.toUpperCase()
            }));
        },
        filteredFlagOptions: function () {
            if (!this.flagSearch) {
                return this.allFlagOptions;
            }
            const query = this.flagSearch.toLowerCase().trim();
            return this.allFlagOptions.filter(flag =>
                flag.code.toLowerCase().includes(query) ||
                flag.name.toLowerCase().includes(query)
            );
        }
    },
    methods: {
        getFlagName: function (code) {
            if (!code) return '';
            return flagNames[code] || code.toUpperCase();
        },
        onSelectFlagCustom: async function (code) {
            if (!code) return;
            this.selectedFlagCode = code;
            this.selectedFlagPreviewUrl = '/images/default/language/flags/' + code + '.svg';
            this.isFlagDropdownOpen = false;
            this.flagSearch = '';
            try {
                const response = await fetch(this.selectedFlagPreviewUrl);
                if (response.ok) {
                    const blob = await response.blob();
                    this.image = new File([blob], code + '.svg', { type: 'image/svg+xml' });
                }
            } catch (e) {
                console.error("Error al cargar bandera predeterminada:", e);
            }
        },
        onCodeInput: function () {
            if (this.imageSource === 'default' && this.props.form.code) {
                const cleanCode = this.props.form.code.toLowerCase().trim();
                if (allFlagCodes.includes(cleanCode)) {
                    this.onSelectFlagCustom(cleanCode);
                }
            }
        },
        changeImage: function (e) {
            if (e.target.files && e.target.files[0]) {
                this.image = e.target.files[0];
            }
        },
        reset: function () {
            appService.modalHide();
            this.$store.dispatch("language/reset").then().catch();
            this.errors = {};
            this.selectedFlagCode = null;
            this.selectedFlagPreviewUrl = null;
            this.isFlagDropdownOpen = false;
            this.flagSearch = '';
            this.imageSource = 'default';
            this.$props.props.form = {
                name: "",
                code: "",
                display_mode: displayModeEnum.LTR,
                status: statusEnum.ACTIVE,
            };
            if (this.image) {
                this.image = "";
                if (this.$refs.imageProperty) {
                    this.$refs.imageProperty.value = null;
                }
            }
        },

        save: function () {
            try {
                const fd = new FormData();
                fd.append("name", this.props.form.name);
                fd.append("code", this.props.form.code);
                fd.append("status", this.props.form.status);
                fd.append("display_mode", this.props.form.display_mode);
                if (this.image) {
                    fd.append("image", this.image);
                }

                const tempId = this.$store.getters["language/temp"].temp_id;
                this.loading.isActive = true;
                this.$store.dispatch("language/save", {
                    form: fd,
                    search: this.props.search,
                }).then((res) => {
                    appService.modalHide();
                    this.loading.isActive = false;
                    alertService.successFlip(
                        tempId === null ? 0 : 1,
                        this.$t("menu.languages")
                    );
                    this.props.form = {
                        title: "",
                        description: "",
                        display_mode: displayModeEnum.LTR,
                        status: statusEnum.ACTIVE,
                    };
                    this.image = "";
                    this.selectedFlagCode = null;
                    this.selectedFlagPreviewUrl = null;
                    this.errors = {};
                    if (this.$refs.imageProperty) {
                        this.$refs.imageProperty.value = null;
                    }
                }).catch((err) => {
                    this.loading.isActive = false;
                    if (err.response && err.response.data && err.response.data.errors) {
                        this.errors = err.response.data.errors;
                    }
                });
            } catch (err) {
                this.loading.isActive = false;
                alertService.error(err);
            }
        },
    },
};
</script>
